import { useEffect, useRef, useState, useMemo, type CSSProperties } from "react";
import {
  getCompAuditQueue,
  setCompAuditStatus,
  getCompSanctionedDiscounts,
  type CompAuditRow,
  type CompAuditC33Status,
  type CompSanctionedDiscount,
} from "../../lib/api";
import OperationsStudioTabs from "../../components/admin/OperationsStudioTabs";

// C33 chip per snapshot status. Members get a tone; guest-resolution outcomes
// stay neutral so a real membership problem is what draws the eye.
const C33_CHIP: Record<CompAuditC33Status, { label: string; tone: "good" | "warn" | "bad" | "neutral" }> = {
  active: { label: "C33 Active", tone: "good" },
  canceled: { label: "C33 Canceled", tone: "warn" },
  purchased: { label: "C33 Gift · not activated", tone: "warn" },
  lapsed: { label: "C33 Lapsed", tone: "bad" },
  expired: { label: "C33 Expired", tone: "bad" },
  non_member: { label: "Not a C33 member", tone: "neutral" },
  unlinked_guest: { label: "C33 unknown · guest not linked", tone: "neutral" },
  no_guest: { label: "C33 unknown · no guest on ticket", tone: "neutral" },
};

const PENDING_LIMIT = 200;
// History loads HISTORY_LIMIT rows per status per page; "Load older" pages with p_offset.
// Search covers only loaded rows.
const HISTORY_LIMIT = 100;
const HISTORY_STATUSES = ["reviewed", "dismissed", "auto_reviewed"] as const;
type HistoryStatus = (typeof HISTORY_STATUSES)[number];
type HistoryMore = Record<HistoryStatus, boolean>;
const NO_MORE: HistoryMore = { reviewed: false, dismissed: false, auto_reviewed: false };

const byReviewedDesc = (a: CompAuditRow, b: CompAuditRow) =>
  new Date(b.reviewed_at || b.created_at).getTime() - new Date(a.reviewed_at || a.created_at).getTime();

// staff_names arrives from the server untyped at runtime; keep only real strings so a
// stray null can't crash search or rendering.
const staffNames = (item: CompAuditRow): string[] =>
  Array.isArray(item.staff_names) ? item.staff_names.filter((n): n is string => typeof n === "string") : [];

// Own-key lookup only: a stray server value like "constructor" must not hit Object.prototype.
const c33Chip = (status: string | null | undefined) =>
  status && Object.prototype.hasOwnProperty.call(C33_CHIP, status)
    ? C33_CHIP[status as CompAuditC33Status]
    : null;

export default function AdminCompAudit() {
  const [activeTab, setActiveTab] = useState<"pending" | "bottle_alert" | "history" | "rules">("pending");
  const [pendingItems, setPendingItems] = useState<CompAuditRow[]>([]);
  const [historyItems, setHistoryItems] = useState<CompAuditRow[]>([]);
  const [rules, setRules] = useState<CompSanctionedDiscount[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Which sections failed on the last load. A failed section keeps its previous rows and
  // never shows its "all clear" empty state.
  const [failed, setFailed] = useState({ pending: false, history: false, rules: false });
  // Per history status: did the last page come back full, so older rows may exist unloaded.
  const [historyMore, setHistoryMore] = useState<HistoryMore>(NO_MORE);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState<string | null>(null);
  // Bumped by every full load. A response from an earlier generation is dropped, so a slow
  // Load older (or a slow earlier load) can't merge stale pages into a refreshed list.
  const loadGen = useRef(0);

  // Each section loads on its own: a failed history call must not discard the pending queue.
  const loadData = async () => {
    const gen = ++loadGen.current;
    setLoading(true);
    setErrorMsg(null);
    setOlderError(null);
    const [pending, reviewed, dismissed, autoReviewed, sanctionedRules] = await Promise.allSettled([
      getCompAuditQueue("pending", PENDING_LIMIT),
      getCompAuditQueue("reviewed", HISTORY_LIMIT),
      getCompAuditQueue("dismissed", HISTORY_LIMIT),
      getCompAuditQueue("auto_reviewed", HISTORY_LIMIT),
      getCompSanctionedDiscounts(),
    ]);
    if (gen !== loadGen.current) return; // a newer load owns the state now
    const history = [reviewed, dismissed, autoReviewed];
    const nextFailed = {
      pending: pending.status === "rejected",
      history: history.some((r) => r.status === "rejected"),
      rules: sanctionedRules.status === "rejected",
    };

    if (pending.status === "fulfilled") setPendingItems(pending.value || []);
    if (!nextFailed.history) {
      const lists = history.map((r) => (r.status === "fulfilled" ? r.value || [] : []));
      setHistoryMore({
        reviewed: lists[0].length >= HISTORY_LIMIT,
        dismissed: lists[1].length >= HISTORY_LIMIT,
        auto_reviewed: lists[2].length >= HISTORY_LIMIT,
      });
      setHistoryItems(lists.flat().sort(byReviewedDesc));
    }
    if (sanctionedRules.status === "fulfilled") setRules(sanctionedRules.value || []);

    const firstErr = [pending, ...history, sanctionedRules].find((r) => r.status === "rejected") as
      | PromiseRejectedResult
      | undefined;
    if (firstErr) {
      const parts = [nextFailed.pending && "pending queue", nextFailed.history && "history", nextFailed.rules && "sanctioned rules"]
        .filter(Boolean)
        .join(", ");
      setErrorMsg(`Couldn't load ${parts}: ${firstErr.reason?.message || "network error"}`);
    }
    setFailed(nextFailed);
    setLoading(false);
  };

  // Next page of every history status that may have more, offset by what's loaded for that
  // status. Offset paging can shift if statuses change between pages, so rows are de-duplicated
  // by id; a row that moved mid-paging can be missed until the next Refresh.
  const loadOlder = async () => {
    const statuses = HISTORY_STATUSES.filter((s) => historyMore[s]);
    if (!statuses.length) return;
    const gen = loadGen.current;
    setLoadingOlder(true);
    setOlderError(null);
    const results = await Promise.allSettled(
      statuses.map((s) =>
        getCompAuditQueue(s, HISTORY_LIMIT, historyItems.filter((i) => i.status === s).length)
      )
    );
    if (gen !== loadGen.current) {
      // Refresh (or a status change) reloaded history meanwhile; these offsets no longer apply.
      setLoadingOlder(false);
      return;
    }
    const nextMore = { ...historyMore };
    const added: CompAuditRow[] = [];
    let err: string | null = null;
    results.forEach((r, i) => {
      if (r.status === "fulfilled") {
        const rows = r.value || [];
        nextMore[statuses[i]] = rows.length >= HISTORY_LIMIT;
        added.push(...rows);
      } else {
        err = r.reason?.message || "network error";
      }
    });
    setHistoryItems((prev) => {
      const seenIds = new Set(prev.map((i) => i.id));
      return [...prev, ...added.filter((i) => !seenIds.has(i.id))].sort(byReviewedDesc);
    });
    setHistoryMore(nextMore);
    setOlderError(err ? `Couldn't load older history: ${err}` : null);
    setLoadingOlder(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSetStatus = async (id: string, newStatus: "pending" | "reviewed" | "dismissed") => {
    setActionInProgress(id);
    try {
      const res = await setCompAuditStatus(id, newStatus);
      if (res.ok) {
        await loadData();
      } else {
        alert("Action failed: " + (res.error || "Unknown error"));
      }
    } catch (err: any) {
      alert("Error updating comp status: " + (err?.message || "Network error"));
    } finally {
      setActionInProgress(null);
    }
  };

  const pendingReviewCount = useMemo(
    () => pendingItems.filter((i) => i.severity === "review").length,
    [pendingItems]
  );
  const bottleAlertCount = useMemo(
    () => pendingItems.filter((i) => i.severity === "bottle_alert").length,
    [pendingItems]
  );
  const reviewedCount = useMemo(
    () => historyItems.filter((i) => i.status === "reviewed" || i.status === "auto_reviewed").length,
    [historyItems]
  );

  // The active tab's rows before search, so an empty result can say whether the list itself
  // is empty (all clear / failed to load) or the search just matched nothing.
  const sourceItems = useMemo(() => {
    if (activeTab === "pending") return pendingItems.filter((i) => i.severity === "review");
    if (activeTab === "bottle_alert") return pendingItems.filter((i) => i.severity === "bottle_alert");
    if (activeTab === "history") return historyItems;
    return [];
  }, [activeTab, pendingItems, historyItems]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sourceItems;
    return sourceItems.filter((item) => {
      const orderMatch = (item.square_order_id || "").toLowerCase().includes(q);
      const nameMatch = (item.discount_name || "").toLowerCase().includes(q);
      const lineMatch = (item.line_items || []).some((l) => (l.name || "").toLowerCase().includes(q));
      const guestMatch = (item.customer_name || "").toLowerCase().includes(q);
      const staffMatch = staffNames(item).some((n) => n.toLowerCase().includes(q));
      const c33Match = (c33Chip(item.c33_status)?.label || "").toLowerCase().includes(q);
      return orderMatch || nameMatch || lineMatch || guestMatch || staffMatch || c33Match;
    });
  }, [sourceItems, search]);

  // Guest label, most specific first. payment_synced undefined = RPC predates the
  // people columns (migration not applied yet) → caller hides the row entirely.
  const guestLabel = (item: CompAuditRow) => {
    if (item.payment_synced === false) return "Pending payment sync";
    if (item.customer_name) return item.customer_name;
    if (item.has_square_customer) {
      return item.tender_summary ? `${item.tender_summary} (no profile)` : "Square guest (no profile)";
    }
    return "Walk-in";
  };
  const staffLabel = (item: CompAuditRow) => {
    if (item.payment_synced === false) return "Pending payment sync";
    const names = staffNames(item);
    return names.length ? names.join(", ") : "Unknown";
  };
  const reviewedHasMore = historyMore.reviewed || historyMore.auto_reviewed;
  const historyHasMore = HISTORY_STATUSES.some((s) => historyMore[s]);

  const fmtCents = (cents: number) => "$" + ((cents || 0) / 100).toFixed(2);
  const fmtDate = (iso: string | null) => {
    if (!iso) return "N/A";
    try {
      return new Date(iso).toLocaleString("en-US", {
        timeZone: "America/Chicago",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }) + " CT";
    } catch {
      return iso;
    }
  };

  return (
    <div style={styles.container}>
      <OperationsStudioTabs activeTab="compAudit" badgeCounts={failed.pending ? {} : { compAudit: pendingItems.length }} />
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Comp Audit & Discount Queue</h1>
          <p style={styles.subtitle}>
            Review non-standard POS comps, missed bottle alerts, and manage sanctioned discounts
          </p>
        </div>
        <button style={styles.refreshBtn} onClick={loadData} disabled={loading}>
          {loading ? "Refreshing..." : "↻ Refresh"}
        </button>
      </div>

      {errorMsg && <div style={styles.errorBanner}>{errorMsg}</div>}

      {/* KPI Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statVal}>{failed.pending ? "—" : pendingReviewCount}</div>
          <div style={styles.statLabel}>Pending Review</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statVal, color: "var(--red, #E05040)" }}>{failed.pending ? "—" : bottleAlertCount}</div>
          <div style={styles.statLabel}>Bottle Alerts</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statVal, color: "var(--green, #27ae60)" }}>
            {failed.history ? "—" : `${reviewedCount}${reviewedHasMore ? "+" : ""}`}
          </div>
          <div style={styles.statLabel}>{reviewedHasMore ? "Reviewed (loaded)" : "Reviewed"}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statVal}>{failed.rules ? "—" : rules.length}</div>
          <div style={styles.statLabel}>Sanctioned Rules</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabBar}>
        <button
          style={{
            ...styles.tabBtn,
            ...(activeTab === "pending" ? styles.tabBtnActive : {}),
          }}
          onClick={() => setActiveTab("pending")}
        >
          Pending Review
          <span style={{ ...styles.badge, background: "var(--gold, #F9AF36)", color: "#111" }}>
            {failed.pending ? "—" : pendingReviewCount}
          </span>
        </button>
        <button
          style={{
            ...styles.tabBtn,
            ...(activeTab === "bottle_alert" ? styles.tabBtnActive : {}),
          }}
          onClick={() => setActiveTab("bottle_alert")}
        >
          Bottle Alerts
          <span style={{ ...styles.badge, background: "var(--red, #E05040)", color: "#fff" }}>
            {failed.pending ? "—" : bottleAlertCount}
          </span>
        </button>
        <button
          style={{
            ...styles.tabBtn,
            ...(activeTab === "history" ? styles.tabBtnActive : {}),
          }}
          onClick={() => setActiveTab("history")}
        >
          Reviewed, Auto & Dismissed
        </button>
        <button
          style={{
            ...styles.tabBtn,
            ...(activeTab === "rules" ? styles.tabBtnActive : {}),
          }}
          onClick={() => setActiveTab("rules")}
        >
          Sanctioned Rules ({failed.rules ? "—" : rules.length})
        </button>
      </div>

      {/* Main Tab Content */}
      {activeTab === "rules" ? (
        <div style={styles.section}>
          <p style={styles.rulesIntro}>
            Discounts matching these rules are automatically categorized as <strong>sanctioned</strong> and excluded from alerting or manual review.
          </p>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.thRow}>
                  <th style={styles.th}>Match Type</th>
                  <th style={styles.th}>Pattern / ID</th>
                  <th style={styles.th}>Category Scoped</th>
                  <th style={styles.th}>Note</th>
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={styles.emptyTd}>
                      {failed.rules ? "Couldn't load sanctioned rules." : "No sanctioned rules found."}
                    </td>
                  </tr>
                ) : (
                  rules.map((r) => (
                    <tr key={r.id} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={styles.tagSource}>{r.match_type}</span>
                      </td>
                      <td style={styles.td}>
                        <code style={styles.code}>{r.match_value}</code>
                      </td>
                      <td style={styles.td}>
                        {r.category_scoped ? (
                          <span style={styles.tagWarn}>YES (C33 Pours)</span>
                        ) : (
                          <span style={{ color: "var(--muted)" }}>No</span>
                        )}
                      </td>
                      <td style={{ ...styles.td, color: "var(--muted)" }}>{r.note || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div style={styles.section}>
          {/* Toolbar */}
          <div style={styles.toolbar}>
            <input
              type="text"
              style={styles.search}
              placeholder="Search order ID, discount, item, guest, staff, C33 status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {activeTab === "history" && historyHasMore && (
            <p style={styles.rulesIntro}>
              {historyItems.length} loaded. Older records aren't loaded or searched yet; use Load older below.
            </p>
          )}

          {/* List */}
          {loading ? (
            <div style={styles.empty}>Loading comp audit queue...</div>
          ) : sourceItems.length === 0 && (activeTab === "history" ? failed.history : failed.pending) ? (
            <div style={styles.empty}>
              Couldn't load {activeTab === "history" ? "history" : "the pending queue"}. Use ↻ Refresh to try again.
            </div>
          ) : filteredItems.length === 0 && sourceItems.length > 0 ? (
            <div style={styles.empty}>No comps match “{search.trim()}”.</div>
          ) : filteredItems.length === 0 ? (
            <div style={styles.empty}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>
                {activeTab === "pending" ? "🎉" : activeTab === "bottle_alert" ? "🍷" : "📋"}
              </div>
              {activeTab === "pending"
                ? "All pending non-standard comps have been reviewed!"
                : activeTab === "bottle_alert"
                ? "No unreviewed bottle alerts."
                : "No history found."}
            </div>
          ) : (
            <div style={styles.list}>
              {filteredItems.map((item) => {
                const isBottle = item.severity === "bottle_alert";
                const isAuto = item.status === "auto_reviewed";
                return (
                  <div
                    key={item.id}
                    style={{
                      ...styles.card,
                      borderLeft: isBottle ? "4px solid var(--red, #E05040)" : "4px solid var(--gold, #F9AF36)",
                    }}
                  >
                    <div style={styles.cardHeader}>
                      <div style={{ minWidth: 0 }}>
                        <span style={styles.cardTitle}>{item.discount_name || "Unnamed Discount"}</span>
                        <span
                          style={{
                            ...styles.tag,
                            ...(isBottle ? styles.tagBottle : styles.tagReview),
                            marginLeft: 8,
                          }}
                        >
                          {isBottle ? "🚨 Bottle Alert" : "📋 Review"}
                        </span>
                        <span style={{ ...styles.tagSource, marginLeft: 6 }}>
                          {item.source || "webhook"}
                        </span>
                        {isAuto && (
                          <span style={{ ...styles.tagSource, marginLeft: 6 }}>Auto · C33</span>
                        )}
                      </div>
                      <div style={styles.cardAmount}>{fmtCents(item.applied_cents)}</div>
                    </div>

                    <div style={styles.cardMeta}>
                      Order: <code style={styles.code}>{item.square_order_id}</code>
                      &nbsp;·&nbsp; Closed: {fmtDate(item.order_closed_at)}
                    </div>

                    {item.payment_synced !== undefined && (
                      <div style={styles.cardPeople}>
                        <span style={styles.personItem}>
                          Guest: <strong style={styles.personName}>{guestLabel(item)}</strong>
                        </span>
                        <span style={styles.personItem}>
                          Rung by: <strong style={styles.personName}>{staffLabel(item)}</strong>
                        </span>
                        {item.guest_is_staff === true && (
                          <span
                            style={styles.tagWarn}
                            title="The guest's customer account is a staff member who took payment on this ticket."
                          >
                            Own ticket
                          </span>
                        )}
                        {c33Chip(item.c33_status) && (
                          <span
                            style={{ ...styles.c33Chip, ...styles[`c33_${c33Chip(item.c33_status)!.tone}`] }}
                            title="C33 membership status when this comp was recorded"
                          >
                            {c33Chip(item.c33_status)!.label}
                          </span>
                        )}
                        {item.c33_billing_health === "lapsed" && (
                          <span style={{ ...styles.c33Chip, ...styles.c33_bad }}>Billing lapsed</span>
                        )}
                        {item.tender_summary && item.customer_name && (
                          <span style={styles.personItem}>Paid: {item.tender_summary}</span>
                        )}
                      </div>
                    )}

                    {item.line_items && item.line_items.length > 0 && (
                      <div style={styles.linesBox}>
                        {item.line_items.map((li, idx) => (
                          <div key={idx} style={styles.lineItem}>
                            <span>
                              {li.is_bottle ? "🍷 " : ""}
                              <strong>{li.name || "Item"}</strong>
                            </span>
                            <span>{li.applied_cents ? fmtCents(li.applied_cents) : ""}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={styles.cardFooter}>
                      {item.status === "pending" ? (
                        <>
                          <button
                            style={styles.btnPrimary}
                            disabled={actionInProgress === item.id}
                            onClick={() => handleSetStatus(item.id, "reviewed")}
                          >
                            ✓ Mark Reviewed
                          </button>
                          <button
                            style={styles.btnSecondary}
                            disabled={actionInProgress === item.id}
                            onClick={() => handleSetStatus(item.id, "dismissed")}
                          >
                            ✕ Dismiss
                          </button>
                        </>
                      ) : (
                        <>
                          <span style={styles.statusLabel}>
                            Status: <strong>{isAuto ? "auto-reviewed" : item.status}</strong> ({fmtDate(item.reviewed_at)})
                          </span>
                          {/* Auto comps were applied by the system and never queued; reopening
                              would relabel them as manual pending comps. */}
                          {!isAuto && (
                            <button
                              style={styles.btnSecondary}
                              disabled={actionInProgress === item.id}
                              onClick={() => handleSetStatus(item.id, "pending")}
                            >
                              ↺ Reopen
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === "history" && olderError && <div style={styles.errorBanner}>{olderError}</div>}
          {activeTab === "history" && historyHasMore && !loading && (
            <div style={styles.loadOlder}>
              <button style={styles.btnSecondary} onClick={loadOlder} disabled={loadingOlder}>
                {loadingOlder ? "Loading…" : `Load older (next ${HISTORY_LIMIT} per status)`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    maxWidth: 1040,
    margin: "0 auto",
    padding: "24px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 24,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 4px 0",
  },
  subtitle: {
    fontSize: 13,
    color: "var(--muted, #8A7A66)",
    margin: 0,
  },
  refreshBtn: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 6,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  errorBanner: {
    background: "rgba(224, 80, 64, 0.15)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    color: "#ff8b7b",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 24,
  },
  statCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "16px 12px",
    textAlign: "center",
  },
  statVal: {
    fontFamily: "Georgia, serif",
    fontSize: 26,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  statLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "var(--muted, #8A7A66)",
    marginTop: 4,
  },
  tabBar: {
    display: "flex",
    gap: 6,
    borderBottom: "1px solid var(--border, #2A1F14)",
    marginBottom: 20,
    overflowX: "auto",
  },
  tabBtn: {
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 600,
    color: "var(--muted, #8A7A66)",
    cursor: "pointer",
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    display: "flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  },
  tabBtnActive: {
    color: "var(--gold, #F9AF36)",
    borderBottomColor: "var(--gold, #F9AF36)",
  },
  badge: {
    padding: "2px 6px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 700,
  },
  section: {},
  toolbar: {
    marginBottom: 16,
    display: "flex",
    gap: 10,
  },
  search: {
    flex: 1,
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    padding: "8px 12px",
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
  },
  empty: {
    textAlign: "center",
    padding: "48px 24px",
    color: "var(--muted, #8A7A66)",
    background: "var(--surface, #13100B)",
    borderRadius: 8,
    border: "1px solid var(--border, #2A1F14)",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  card: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: 16,
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 6,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: "var(--text, #F0E6D4)",
    overflowWrap: "anywhere",
  },
  cardAmount: {
    fontFamily: "Georgia, serif",
    fontSize: 18,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  cardMeta: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    marginBottom: 10,
  },
  cardPeople: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "4px 16px",
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    marginBottom: 10,
  },
  c33Chip: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 700,
    border: "1px solid transparent",
  },
  c33_good: {
    background: "rgba(39, 174, 96, 0.15)",
    color: "var(--green, #27ae60)",
    borderColor: "rgba(39, 174, 96, 0.35)",
  },
  c33_warn: {
    background: "rgba(249, 175, 54, 0.15)",
    color: "var(--gold, #F9AF36)",
    borderColor: "rgba(249, 175, 54, 0.35)",
  },
  c33_bad: {
    background: "rgba(224, 80, 64, 0.15)",
    color: "#ff7666",
    borderColor: "rgba(224, 80, 64, 0.35)",
  },
  c33_neutral: {
    background: "rgba(255, 255, 255, 0.05)",
    color: "var(--muted, #8A7A66)",
    borderColor: "var(--border, #2A1F14)",
  },
  // Guest/staff names are free text from Square; a long unbroken one must wrap inside the
  // card instead of widening it past a 375px screen.
  personItem: {
    minWidth: 0,
    maxWidth: "100%",
    overflowWrap: "anywhere",
  },
  personName: {
    color: "var(--text, #F0E6D4)",
    fontWeight: 600,
  },
  code: {
    fontFamily: "monospace",
    color: "var(--gold, #F9AF36)",
  },
  tag: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  tagBottle: {
    background: "rgba(224, 80, 64, 0.15)",
    color: "#ff7666",
    border: "1px solid rgba(224, 80, 64, 0.3)",
  },
  tagReview: {
    background: "rgba(249, 175, 54, 0.15)",
    color: "var(--gold, #F9AF36)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
  },
  tagSource: {
    display: "inline-block",
    padding: "2px 6px",
    borderRadius: 4,
    fontSize: 10,
    textTransform: "uppercase",
    background: "rgba(255, 255, 255, 0.05)",
    color: "var(--muted, #8A7A66)",
    border: "1px solid var(--border, #2A1F14)",
  },
  tagWarn: {
    display: "inline-block",
    padding: "2px 6px",
    borderRadius: 4,
    fontSize: 11,
    background: "rgba(249, 175, 54, 0.15)",
    color: "var(--gold, #F9AF36)",
    fontWeight: 600,
  },
  linesBox: {
    background: "rgba(0, 0, 0, 0.2)",
    borderRadius: 6,
    padding: "8px 12px",
    marginBottom: 12,
    fontSize: 12,
  },
  lineItem: {
    display: "flex",
    justifyContent: "space-between",
    padding: "4px 0",
    borderBottom: "1px solid rgba(255, 255, 255, 0.04)",
  },
  cardFooter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
  },
  statusLabel: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    marginRight: 8,
  },
  btnPrimary: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 5,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  btnSecondary: {
    background: "transparent",
    color: "var(--text, #F0E6D4)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  loadOlder: {
    display: "flex",
    justifyContent: "center",
    marginTop: 16,
  },
  rulesIntro: {
    fontSize: 13,
    color: "var(--muted, #8A7A66)",
    marginBottom: 14,
  },
  tableWrap: {
    overflowX: "auto",
    background: "var(--surface, #13100B)",
    borderRadius: 8,
    border: "1px solid var(--border, #2A1F14)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    textAlign: "left",
  },
  thRow: {
    borderBottom: "1px solid var(--border, #2A1F14)",
  },
  th: {
    padding: "12px 14px",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "var(--muted, #8A7A66)",
  },
  tr: {
    borderBottom: "1px solid rgba(255, 255, 255, 0.04)",
  },
  td: {
    padding: "12px 14px",
  },
  emptyTd: {
    padding: 24,
    textAlign: "center",
    color: "var(--muted, #8A7A66)",
  },
};
